import type React from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { validateCardholderName } from '@features/Stripe/utils/validate-cardholder-name';

import styles from './CardholderName.module.css';

type CardholderNameProps = {
  name: string;
  onNameUpdate: (name: string) => void;
  onValidationUpdate: (isValid: boolean, message: string) => void;
};

export const CardholderName = memo(
  ({ name, onNameUpdate, onValidationUpdate }: CardholderNameProps) => {
    const handleNameChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value.toUpperCase();
        onNameUpdate(newValue);
      },
      [onNameUpdate],
    );

    const validation = useMemo(() => {
      const isValidName = validateCardholderName(name);
      if (name.length === 0) {
        return { isValid: false, message: '' };
      }
      if (isValidName && name.length > 0) {
        return { isValid: true, message: '' };
      } else {
        return {
          isValid: false,
          message: 'Please enter the full name and surname',
        };
      }
    }, [name]);

    useEffect(() => {
      onValidationUpdate(validation.isValid, validation.message);
    }, [validation, onValidationUpdate]);

    return (
      <div className={styles.nameElement}>
        <input
          className={styles.input}
          id="name"
          required
          style={{
            color: validation.isValid ? '#302C28' : '#FF2E00',
          }}
          type="text"
          placeholder="Cardholder Name"
          value={name}
          onChange={handleNameChange}
        />
      </div>
    );
  },
);
